/**
 * Voice-note recorder: MediaRecorder (WebM/Opus only) plus the cursor /
 * viewport track sampled from the WSI viewer on the same clock.
 *
 * Page-level so a recording survives right-rail tab switches and keeps
 * running while the annotator works in any other module. A finished note is
 * encoded as ONE `VoiceNoteFileV1` CBOR document and posted immediately with
 * an `application/cbor` body; it is not part of the session history.
 */
import { useApiClient } from "@frontend/hooks/use-api"
import { isTextEntryTarget } from "@frontend/pages/case-workspace/pathology/hooks/use-overlay-peek"
import { describeMicError } from "@frontend/pages/case-workspace/pathology/lib/audio-input-devices"
import {
  blobsToBytes,
  buildAudioConstraints,
  getVoiceRecordingUnsupportedReason,
  RecordingClock,
  watchDeadInput
} from "@frontend/pages/case-workspace/pathology/lib/voice-note-recording"
import { encodeVoiceNoteFile, VOICE_NOTE_MIME_TYPE, VoiceNoteTrackBuilder } from "@shared/extensions/voice-note/file"
import type { VoiceNoteApiItem } from "@shared/extensions/voice-note/handler"
import { useQueryClient } from "@tanstack/react-query"
import type { PointerMoveEvent, TileViewer, ViewportChangeEvent } from "@virdx/wsi-viewer"
import { encode } from "cbor-x"
import { type RefObject, useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"

export type VoiceNoteRecorderStatus = "idle" | "requesting" | "recording" | "paused" | "uploading" | "upload-failed"

/** Same cadence as the multiplayer cursor broadcast (`use-presence.ts`). */
const POINTER_SAMPLE_INTERVAL_MS = 33
const VIEWPORT_SAMPLE_INTERVAL_MS = 50
const ELAPSED_TICK_MS = 250
const SERIES_CHANGE_TOAST_ID = "voice-note-series-change"

export const voiceNotesQueryKey = (caseId: string | undefined) => ["case-annotations", caseId ?? "", "voice_note"]

interface UseVoiceNoteRecorderParams {
  viewerRef: RefObject<TileViewer | null>
  viewerEpoch: number
  caseId: string | undefined
  submissionId: string | null
  projectModuleId: string | null
  activeSeriesId: string | null
  /** Own in-progress submission, not review / view-only / bootstrapping, module present. */
  canRecord: boolean
  /** Microphone to open (`null` = system default). */
  deviceId: string | null
}

interface PendingUpload {
  bytes: Uint8Array
  submissionId: string
  projectModuleId: string
  seriesId: string
}

export interface VoiceNoteRecorderState {
  status: VoiceNoteRecorderStatus
  isRecording: boolean
  elapsedMs: number
  /** Non-null when this browser cannot record WebM/Opus. */
  unsupportedReason: string | null
  /** The open microphone stream while recording (for the level meter). */
  stream: MediaStream | null
  title: string
  setTitle: (value: string) => void
  caption: string
  setCaption: (value: string) => void
  start: () => Promise<void>
  pause: () => void
  resume: () => void
  stop: () => void
  retryUpload: () => Promise<void>
  discard: () => void
}

export function useVoiceNoteRecorder({
  viewerRef,
  viewerEpoch,
  caseId,
  submissionId,
  projectModuleId,
  activeSeriesId,
  canRecord,
  deviceId
}: UseVoiceNoteRecorderParams): VoiceNoteRecorderState {
  const api = useApiClient()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<VoiceNoteRecorderStatus>("idle")
  const [elapsedMs, setElapsedMs] = useState(0)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [title, setTitle] = useState("")
  const [caption, setCaption] = useState("")
  const [unsupportedReason] = useState<string | null>(() => getVoiceRecordingUnsupportedReason())

  const statusRef = useRef(status)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const [clock] = useState(() => new RecordingClock())
  const clockRef = useRef(clock)
  const trackRef = useRef<VoiceNoteTrackBuilder | null>(null)
  const startedAtRef = useRef<string>("")
  const recordedSeriesIdRef = useRef<string | null>(null)
  const imageSizeRef = useRef<{ width: number; height: number } | null>(null)
  const lastPointerSampleRef = useRef(0)
  const lastViewportSampleRef = useRef(0)
  const lastViewportRef = useRef<ViewportChangeEvent | null>(null)
  const pendingUploadRef = useRef<PendingUpload | null>(null)
  const deadInputWatchStopRef = useRef<(() => void) | null>(null)
  const metaRef = useRef({ title: "", caption: "" })
  const contextRef = useRef({ submissionId, projectModuleId, deviceId, canRecord })

  useEffect(() => {
    statusRef.current = status
  }, [status])
  useEffect(() => {
    metaRef.current = { title, caption }
  }, [title, caption])
  useEffect(() => {
    contextRef.current = { submissionId, projectModuleId, deviceId, canRecord }
  }, [submissionId, projectModuleId, deviceId, canRecord])

  const isRecording = status === "recording" || status === "paused"

  const releaseStream = useCallback(() => {
    deadInputWatchStopRef.current?.()
    deadInputWatchStopRef.current = null
    const current = streamRef.current
    if (current) {
      for (const track of current.getTracks()) track.stop()
    }
    streamRef.current = null
    setStream(null)
  }, [])

  const upload = useCallback(
    async (pending: PendingUpload) => {
      setStatus("uploading")
      try {
        await api(`/submissions/${pending.submissionId}/annotations`, {
          method: "POST",
          headers: { "Content-Type": "application/cbor" },
          // Copy into a plain ArrayBuffer-backed view: cbor-x may return a Buffer, which fetch's BodyInit typing rejects.
          body: new Uint8Array(
            encode({
              module_type: "voice_note",
              project_module_id: pending.projectModuleId,
              series_id: pending.seriesId,
              data: { file: pending.bytes }
            })
          )
        })
        pendingUploadRef.current = null
        setTitle("")
        setCaption("")
        setStatus("idle")
        toast.success("Voice note saved")
        await queryClient.invalidateQueries({ queryKey: voiceNotesQueryKey(caseId) })
      } catch (error) {
        pendingUploadRef.current = pending
        setStatus("upload-failed")
        toast.error("Voice note upload failed", {
          description:
            error instanceof Error ? error.message : "The recording is kept in memory; retry from the Voice tab."
        })
      }
    },
    [api, caseId, queryClient]
  )

  /** Called from MediaRecorder.onstop: seal the track and hand the file to the uploader. */
  const finalize = useCallback(
    async (mimeType: string) => {
      const builder = trackRef.current
      const seriesId = recordedSeriesIdRef.current
      const size = imageSizeRef.current
      const submission = contextRef.current.submissionId
      const moduleId = contextRef.current.projectModuleId
      const durationMs = Math.round(clockRef.current.now())
      const chunks = chunksRef.current
      chunksRef.current = []
      trackRef.current = null
      releaseStream()

      if (!(builder && seriesId && size && submission && moduleId)) {
        setStatus("idle")
        return
      }
      // Close the viewport track with the final camera position.
      const last = lastViewportRef.current
      if (last) builder.pushViewport(durationMs, last.zoom, last.centerX, last.centerY)

      const audio = await blobsToBytes(chunks, mimeType)
      if (audio.byteLength === 0) {
        setStatus("idle")
        toast.warning("Nothing was recorded")
        return
      }
      const meta = metaRef.current
      const bytes = encodeVoiceNoteFile({
        version: 1,
        seriesId,
        imageWidth: size.width,
        imageHeight: size.height,
        startedAt: startedAtRef.current,
        durationMs,
        mimeType: VOICE_NOTE_MIME_TYPE,
        title: meta.title.trim() || null,
        caption: meta.caption.trim() || null,
        audio,
        ...builder.finish()
      })
      await upload({ bytes, submissionId: submission, projectModuleId: moduleId, seriesId })
    },
    [releaseStream, upload]
  )

  const stop = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || statusRef.current === "idle") return
    clockRef.current.pause()
    recorderRef.current = null
    if (recorder.state !== "inactive") {
      recorder.stop() // → onstop → finalize()
    } else {
      void finalize(recorder.mimeType || VOICE_NOTE_MIME_TYPE)
    }
  }, [finalize])

  const start = useCallback(async () => {
    const ctx = contextRef.current
    if (statusRef.current !== "idle" && statusRef.current !== "upload-failed") return
    if (unsupportedReason) {
      toast.error("Recording unavailable", { description: unsupportedReason })
      return
    }
    const viewer = viewerRef.current
    const level0 = viewer?.getImageInfo()?.levels[0]
    if (!(ctx.canRecord && ctx.submissionId && ctx.projectModuleId && activeSeriesId && viewer && level0)) {
      toast.info("Open a slide before recording a voice note")
      return
    }
    if (pendingUploadRef.current) {
      toast.info("Retry or discard the failed upload before recording a new note")
      return
    }

    setStatus("requesting")
    let mediaStream: MediaStream
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia(buildAudioConstraints(ctx.deviceId))
    } catch (error) {
      if (ctx.deviceId && error instanceof Error && error.name === "OverconstrainedError") {
        // The remembered microphone is gone: fall back to the system default.
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia(buildAudioConstraints(null))
          toast.info("Selected microphone unavailable, using the system default")
        } catch (fallbackError) {
          setStatus("idle")
          toast.error("Microphone unavailable", { description: describeMicError(fallbackError) })
          return
        }
      } else {
        setStatus("idle")
        toast.error("Microphone unavailable", { description: describeMicError(error) })
        return
      }
    }

    let recorder: MediaRecorder
    try {
      // Browser-default Opus bitrate (~128-160 kbps, ~70 MB per hour). A 32 kbps
      // cap was tried and sounded noticeably worse on playback, so it stays uncapped.
      recorder = new MediaRecorder(mediaStream, { mimeType: VOICE_NOTE_MIME_TYPE })
    } catch (error) {
      for (const track of mediaStream.getTracks()) track.stop()
      setStatus("idle")
      toast.error("Recording unavailable", { description: describeMicError(error) })
      return
    }

    chunksRef.current = []
    trackRef.current = new VoiceNoteTrackBuilder()
    startedAtRef.current = new Date().toISOString()
    recordedSeriesIdRef.current = activeSeriesId
    imageSizeRef.current = { width: level0.width, height: level0.height }
    lastPointerSampleRef.current = -Infinity
    lastViewportSampleRef.current = -Infinity
    const center = viewer.getCenter()
    const initialViewport: ViewportChangeEvent = {
      zoom: viewer.getZoom(),
      centerX: center.x,
      centerY: center.y,
      level: viewer.getCurrentLevel(),
      viewport: viewer.getViewport()
    }
    lastViewportRef.current = initialViewport
    trackRef.current.pushViewport(0, initialViewport.zoom, initialViewport.centerX, initialViewport.centerY)

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) chunksRef.current.push(event.data)
    }
    recorder.onstop = () => {
      void finalize(recorder.mimeType || VOICE_NOTE_MIME_TYPE)
    }
    recorder.onerror = () => {
      toast.error("Recording error", { description: "The recording stopped unexpectedly; saving what was captured." })
      stop()
    }
    // Device unplugged / Bluetooth disconnected mid-recording: keep what we have.
    for (const track of mediaStream.getAudioTracks()) {
      track.onended = () => {
        if (recorderRef.current === recorder) {
          toast.warning("Microphone disconnected", { description: "Saving the recording captured so far." })
          stop()
        }
      }
    }

    streamRef.current = mediaStream
    setStream(mediaStream)
    recorderRef.current = recorder
    clockRef.current.start()
    recorder.start(1000)
    setElapsedMs(0)
    setStatus("recording")
    // A device the OS is not capturing from records silence without any error.
    deadInputWatchStopRef.current = watchDeadInput(mediaStream)
  }, [activeSeriesId, finalize, stop, unsupportedReason, viewerRef])

  const pause = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || statusRef.current !== "recording" || recorder.state !== "recording") return
    recorder.pause()
    clockRef.current.pause()
    setStatus("paused")
  }, [])

  const resume = useCallback(() => {
    const recorder = recorderRef.current
    if (!recorder || statusRef.current !== "paused" || recorder.state !== "paused") return
    recorder.resume()
    clockRef.current.resume()
    setStatus("recording")
  }, [])

  const retryUpload = useCallback(async () => {
    const pending = pendingUploadRef.current
    if (!pending) return
    await upload(pending)
  }, [upload])

  const discard = useCallback(() => {
    pendingUploadRef.current = null
    setTitle("")
    setCaption("")
    setStatus("idle")
  }, [])

  // Viewer → track sampling while recording (not while paused: the clock is frozen anyway).
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0 || !isRecording) return

    const onPointerMove = (event: PointerMoveEvent) => {
      const builder = trackRef.current
      if (!(builder && clockRef.current.isRunning)) return
      const t = clockRef.current.now()
      if (t - lastPointerSampleRef.current < POINTER_SAMPLE_INTERVAL_MS) return
      lastPointerSampleRef.current = t
      builder.pushPointer(t, event.imageX, event.imageY)
    }
    const onPointerLeave = () => {
      const builder = trackRef.current
      if (!(builder && clockRef.current.isRunning)) return
      const t = clockRef.current.now()
      lastPointerSampleRef.current = t
      builder.pushPointerLeave(t)
    }
    const onViewportChange = (event: ViewportChangeEvent) => {
      lastViewportRef.current = event
      const builder = trackRef.current
      if (!(builder && clockRef.current.isRunning)) return
      const t = clockRef.current.now()
      if (t - lastViewportSampleRef.current < VIEWPORT_SAMPLE_INTERVAL_MS) return
      lastViewportSampleRef.current = t
      builder.pushViewport(t, event.zoom, event.centerX, event.centerY)
    }

    viewer.on("pointer-move", onPointerMove)
    viewer.on("pointer-leave", onPointerLeave)
    viewer.on("viewport-change", onViewportChange)
    return () => {
      viewer.off("pointer-move", onPointerMove)
      viewer.off("pointer-leave", onPointerLeave)
      viewer.off("viewport-change", onViewportChange)
    }
  }, [viewerRef, viewerEpoch, isRecording])

  // Elapsed display.
  useEffect(() => {
    if (status !== "recording") return
    const timer = setInterval(() => setElapsedMs(Math.round(clockRef.current.now())), ELAPSED_TICK_MS)
    return () => clearInterval(timer)
  }, [status])

  // Switching slides ends the note (the track is in that slide's coordinates).
  useEffect(() => {
    if (!isRecording) return
    if (recordedSeriesIdRef.current && activeSeriesId !== recordedSeriesIdRef.current) {
      toast.info("Recording stopped: the slide changed", { id: SERIES_CHANGE_TOAST_ID })
      stop()
    }
  }, [activeSeriesId, isRecording, stop])

  // Losing the right to record (review mode, submission gone) also ends it.
  useEffect(() => {
    if (isRecording && !canRecord) stop()
  }, [canRecord, isRecording, stop])

  // Unmount / case change: stop and let onstop upload what was captured.
  useEffect(
    () => () => {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop()
      }
      releaseStream()
    },
    [releaseStream]
  )

  // Never lose a recording to an accidental navigation.
  useEffect(() => {
    if (status === "idle") return
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => window.removeEventListener("beforeunload", onBeforeUnload)
  }, [status])

  // `V` toggles start / stop (window-level, ignores text inputs, modifiers and key repeat).
  useEffect(() => {
    if (!canRecord) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "v" && event.key !== "V") return
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return
      if (isTextEntryTarget(event.target)) return
      event.preventDefault()
      const current = statusRef.current
      if (current === "idle") void start()
      else if (current === "recording" || current === "paused") stop()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [canRecord, start, stop])

  return {
    status,
    isRecording,
    elapsedMs,
    unsupportedReason,
    stream,
    title,
    setTitle,
    caption,
    setCaption,
    start,
    pause,
    resume,
    stop,
    retryUpload,
    discard
  }
}

/** Shape of one row from `GET /cases/:caseId/annotations?module_type=voice_note`. */
export interface VoiceNoteListItem extends VoiceNoteApiItem {
  submission_user_id: string
  submission_user_name: string | null
  submission_status: string
}
